import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import { Page } from '@goldcast/player-config/src/types/remotion-types';
import { TextElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';

const processCallOutElements = (
  sanitizedPages: Page[],
  callOutElements: TextElement[],
  captionsOffsetFrames?: number
): Page[] => {
  if (!callOutElements || callOutElements.length === 0) {
    return sanitizedPages;
  }

  const adjustedCallOutElements = callOutElements.map(co => ({
    ...co,
    fromFrame: co.fromFrame - (captionsOffsetFrames || 0),
    toFrame: co.toFrame - (captionsOffsetFrames || 0)
  }));

  // Build exact match lookup for O(1) checks
  const exactMatchMap = new Map<string, TextElement>();
  for (const callOutElement of adjustedCallOutElements) {
    exactMatchMap.set(`${callOutElement.fromFrame}-${callOutElement.toFrame}`, callOutElement);
  }

  // Break regular pages around call-out pages
  const brokenPages: Page[] = [];

  for (const page of sanitizedPages) {
    const matchedCallOut = exactMatchMap.get(`${page.fromFrame}-${page.toFrame}`);
    if (matchedCallOut) {
      brokenPages.push({
        ...page,
        isHidden: matchedCallOut.details.areCaptionsHidden !== false
      });
      continue;
    }

    const overlappingCallOuts: TextElement[] = [];

    for (const callOutElement of adjustedCallOutElements) {
      // Check if ranges overlap: callOutElement.fromFrame < page.toFrame AND callOutElement.toFrame > page.fromFrame
      if (callOutElement.fromFrame < page.toFrame && callOutElement.toFrame > page.fromFrame) {
        overlappingCallOuts.push(callOutElement);
      }
    }

    if (overlappingCallOuts.length === 0) {
      // No overlaps, add the page as-is
      brokenPages.push(page);
      continue;
    }

    // Sort overlapping call-outs by fromFrame to process them chronologically
    overlappingCallOuts.sort((a, b) => a.fromFrame - b.fromFrame);

    const sortedTokens = page.tokens || [];

    // Break the page around overlapping call-outs
    let currentFrame = page.fromFrame;
    let tokenIndex = 0;

    for (const callOutElement of overlappingCallOuts) {
      // Add page segment before call-out (if there's space)
      if (currentFrame < callOutElement.fromFrame) {
        const segmentStart = currentFrame;
        const segmentEnd = callOutElement.fromFrame;

        // Efficient token filtering using sorted tokens
        const beforeTokens: any[] = [];
        while (tokenIndex < sortedTokens.length && sortedTokens[tokenIndex].fromFrame <= segmentEnd) {
          if (sortedTokens[tokenIndex].fromFrame >= segmentStart && sortedTokens[tokenIndex].toFrame <= segmentEnd) {
            beforeTokens.push(sortedTokens[tokenIndex]);
          }
          tokenIndex++;
        }

        brokenPages.push({
          ...page,
          text: beforeTokens.map(token => token.text).join(' '),
          fromFrame: segmentStart,
          toFrame: segmentEnd,
          tokens: beforeTokens
        });

        currentFrame = callOutElement.fromFrame;
      }

      // Add the overlapping call-out section with isHidden: true
      const overlapStart = Math.max(currentFrame, callOutElement.fromFrame);
      const overlapEnd = Math.min(page.toFrame, callOutElement.toFrame);

      if (overlapStart < overlapEnd) {
        // Find tokens within the overlap range
        const overlapTokens: any[] = [];
        for (const token of sortedTokens) {
          if (token.fromFrame >= overlapStart && token.toFrame <= overlapEnd) {
            overlapTokens.push(token);
          }
        }

        brokenPages.push({
          ...page,
          text: overlapTokens.map(token => token.text).join(' '),
          fromFrame: overlapStart,
          toFrame: overlapEnd,
          tokens: overlapTokens,
          isHidden: callOutElement.details.areCaptionsHidden !== false
        });
      }

      currentFrame = callOutElement.toFrame;
    }

    // Add remaining page segment after last call-out (if there's space)
    if (currentFrame < page.toFrame) {
      const segmentStart = currentFrame;
      const segmentEnd = page.toFrame;

      // Efficient token filtering for remaining tokens
      const afterTokens: any[] = [];
      while (tokenIndex < sortedTokens.length && sortedTokens[tokenIndex].fromFrame <= segmentEnd) {
        if (sortedTokens[tokenIndex].fromFrame >= segmentStart && sortedTokens[tokenIndex].toFrame <= segmentEnd) {
          afterTokens.push(sortedTokens[tokenIndex]);
        }
        tokenIndex++;
      }

      brokenPages.push({
        ...page,
        text: afterTokens.map(token => token.text).join(' '),
        fromFrame: segmentStart,
        toFrame: segmentEnd,
        tokens: afterTokens
      });
    }
  }

  // Sort final result by fromFrame and return the modified pages
  brokenPages.sort((a, b) => a.fromFrame - b.fromFrame);
  return brokenPages;
};

export function updatePagesWithCallOutElements(
  pages: Page[],
  clip: Clip,
  updatedElementTracks?: ElementsTrack[],
  captionTrackOffset?: number
): Page[] {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig?.playerConfig?.tracks) {
    return pages;
  }

  const tracks = playerConfig.playerConfig.tracks;

  let elementTracks: ElementsTrack[];
  if (updatedElementTracks && updatedElementTracks.length > 0) {
    elementTracks = updatedElementTracks;
  } else {
    elementTracks = tracks.filter(track => track.type === TrackType.ELEMENTS) as ElementsTrack[];
  }

  const callOutElements = elementTracks.flatMap(track =>
    track.elements.filter((element: { type: ElementType }) => element.type === ElementType.TEXT)
  ) as TextElement[];

  if (callOutElements.length === 0) {
    return pages;
  }

  const processedPages = processCallOutElements(pages, callOutElements, captionTrackOffset);

  return processedPages;
}

/**
 * Pure version of call-out element processing for the runtime path.
 * Pages must already be in global timeline frames; captionsOffsetFrames should be 0.
 */
export function applyCallOutElements(pages: Page[], elementTracks: ElementsTrack[], captionsOffsetFrames = 0): Page[] {
  const callOutElements = elementTracks.flatMap(track =>
    track.elements.filter((element: { type: ElementType }) => element.type === ElementType.TEXT)
  ) as TextElement[];

  if (callOutElements.length === 0) {
    return pages;
  }

  return processCallOutElements(pages, callOutElements, captionsOffsetFrames);
}
